import { useNavigate } from "react-router";

import { TagIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { TableCell, TableRow } from "@/components/ui/table";

import { PRIORITY_CONFIG, STATUS_COLOURS, STATUS_CONFIG, TYPE_CONFIG } from "@/features/tickets/config";
import { TicketListItem } from "@/features/tickets/types/index";

interface Props {
  ticket: TicketListItem;
  projectId: string;
}

export function TicketRow({ ticket, projectId }: Props) {
  const navigate = useNavigate();
  const priority = ticket.priority ? PRIORITY_CONFIG[ticket.priority] : null;
  const status = STATUS_CONFIG[ticket.status];
  const type = TYPE_CONFIG[ticket.type];
  const StatusIcon = status.icon;
  const TypeIcon = type.icon;

  const assigneeName = ticket.assignee ? `${ticket.assignee.firstName} ${ticket.assignee.lastName}` : null;
  const assigneeInitials = ticket.assignee ? `${ticket.assignee.firstName[0]}${ticket.assignee.lastName[0]}` : "";
  const reporterName = ticket.createdBy ? `${ticket.createdBy.firstName ?? ""} ${ticket.createdBy.lastName ?? ""}`.trim() : null;
  const updatedDisplay = (ticket.updatedAt ?? ticket.createdAt).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <TableRow className="cursor-pointer" onClick={() => navigate(`/projects/${projectId}/tickets/${ticket.id}`)}>
      <TableCell className="font-mono text-xs text-muted-foreground">{ticket.key}</TableCell>
      <TableCell>
        <TypeIcon className={`size-4 ${type.className}`} />
      </TableCell>
      <TableCell className="max-w-xs whitespace-normal">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium leading-snug">{ticket.title}</span>
          {ticket.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {ticket.tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                  <TagIcon className="size-2.5 shrink-0" />
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        {priority ? (
          <div className={`flex items-center gap-1.5 text-xs font-medium ${priority.className}`}>
            <priority.icon className="size-3.5 shrink-0" />
            {ticket.priority}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell>
        <div className={`flex items-center gap-1.5 text-xs font-medium ${STATUS_COLOURS[ticket.status]}`}>
          <StatusIcon className="size-3.5 shrink-0" />
          {ticket.status}
        </div>
      </TableCell>
      <TableCell>
        {assigneeName ? (
          <div className="flex items-center gap-2">
            <Avatar size="sm">
              <AvatarImage src="" alt={assigneeName} />
              <AvatarFallback className="text-[10px]">{assigneeInitials}</AvatarFallback>
            </Avatar>
            <span className="text-xs">{assigneeName}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">Unassigned</span>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">{reporterName ?? <span className="italic">Deleted member</span>}</TableCell>
      <TableCell className="text-xs text-muted-foreground tabular-nums">{ticket.commentCount > 0 ? ticket.commentCount : "-"}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{updatedDisplay}</TableCell>
    </TableRow>
  );
}
